/**
 * layout.js — where every stamp goes, decided ONCE for both consumers.
 *
 * WHY THIS FILE EXISTS. The tool has two renderers: the on-page canvas preview
 * and the pdf-lib worker that writes the file. Before this file they each had
 * their own placement maths, and they disagreed — the preview drew one centred
 * stamp for «плитка» while the worker wrote a whole grid, and it ignored the
 * margin logic entirely. A preview that is not the output is worse than no
 * preview: it is a promise the download breaks.
 *
 * So placement lives here, in page coordinates (PDF space: origin bottom-left,
 * y upwards), and both sides import it. The preview converts the result into
 * canvas space at the end; the worker hands it to pdf-lib unchanged.
 *
 * WHAT A PLACEMENT IS. `{x, y, rot}` — the pdf-lib draw anchor, i.e. the
 * bottom-left corner of the UNROTATED stamp box, plus the rotation in degrees
 * about that corner. That is the awkward convention pdf-lib's drawText and
 * drawImage both take, so it is the one that travels; everything inside works
 * with centres, which is the only way to think about "middle-right" or "tile"
 * without going mad.
 *
 * ROTATION IS HONOURED BY THE CORNERS. The old code placed grid positions from
 * the unrotated width, so a rotated stamp in a corner hung off the page —
 * silently, because nothing rendered it before export. Here a corner uses the
 * ROTATED bounding box, so «нижний правый» + 45° stays inside the margin.
 *
 * Exposed as window.WmLayout (page) / self.WmLayout (worker).
 */
(function (root) {
    'use strict';
    if (root.WmLayout) return;

    var DEG = Math.PI / 180;

    /** The axis-aligned box a W×H rectangle occupies once rotated by `deg`. */
    function bbox(w, h, deg) {
        var a = Math.abs(deg * DEG);
        var c = Math.abs(Math.cos(a)), s = Math.abs(Math.sin(a));
        return { w: w * c + h * s, h: w * s + h * c };
    }

    /**
     * Centre → pdf-lib anchor.
     *
     * The stamp's local axes after rotation are u (along the text) and v
     * (up the text); the anchor is half a box back along each.
     */
    function anchor(cx, cy, w, h, deg) {
        var r = deg * DEG, co = Math.cos(r), si = Math.sin(r);
        return {
            x: cx - (w / 2) * co + (h / 2) * si,
            y: cy - (w / 2) * si - (h / 2) * co,
            rot: deg,
        };
    }

    /**
     * Grid anchors. `position` is "<v>-<h>" with v ∈ top|middle|bottom and
     * h ∈ left|center|right, the nine cells of the picker.
     */
    function gridCentre(position, pW, pH, bw, bh, margin) {
        var parts = String(position || 'middle-center').split('-');
        var v = parts[0], h = parts[1];
        var cx = pW / 2, cy = pH / 2;
        if (h === 'left') cx = margin + bw / 2;
        else if (h === 'right') cx = pW - margin - bw / 2;
        if (v === 'top') cy = pH - margin - bh / 2;
        else if (v === 'bottom') cy = margin + bh / 2;
        return { cx: cx, cy: cy };
    }

    function clampCentre(cx, cy, pW, pH, bw, bh) {
        // Half a stamp may hang off a deliberately-dragged placement, but not
        // the whole thing: a watermark nobody can see is not a watermark.
        var padX = Math.min(bw / 2, pW / 2), padY = Math.min(bh / 2, pH / 2);
        return {
            cx: Math.max(padX * 0.15, Math.min(pW - padX * 0.15, cx)),
            cy: Math.max(padY * 0.15, Math.min(pH - padY * 0.15, cy)),
        };
    }

    /**
     * Tile centres.
     *
     * `density` (1…6) is SPACING, not a column count.
     *
     * Columns were the first design and they were wrong in a way worth
     * recording, because it is the same class of defect this whole rewrite is
     * about. The step was `max(box * 1.12, pageWidth / columns)` — the max
     * existing so tiles never overlap — and for any stamp wider than a sixth of
     * the page the box term won at every setting above about three. The slider
     * moved, the number changed, and the output did not: 33 stamps at density
     * 3, 33 at density 6. Caught by the browser test rather than by reading,
     * which is the point of having one.
     *
     * As a spacing factor the control always does something: the gap runs from
     * 2.66 box-widths at the loose end to 1.06 at the tight one, and the tight
     * end is where "no overlap" already lives, so the bound is the end of the
     * scale instead of a silent ceiling in the middle of it.
     *
     * VERTICALLY THE GAP IS COPIED, NOT THE FACTOR. Multiplying the factor by
     * the box HEIGHT was the obvious thing and it fails on the commonest
     * watermark there is: at -45 degrees the bounding box becomes roughly
     * square, so its height jumps from 58 pt to 182, and a factor meant for a
     * short line of text pushed the rows 800 pt apart — two stamps on an A4
     * page at "density 3". Taking the horizontal GAP and reusing it keeps the
     * pattern even at every angle, which is the only thing the person is
     * actually asking for.
     */
    function tileCentres(pW, pH, bw, bh, density) {
        var d = Math.max(1, Math.min(6, Math.round(density || 3)));
        var f = 1.06 + (6 - d) * 0.32;
        var stepX = Math.max(6, bw * f);
        var stepY = Math.max(6, bh * 1.15, bh + 0.8 * bw * (f - 1));
        var out = [];
        for (var y = stepY / 2; y < pH + stepY * 0.35; y += stepY) {
            for (var x = stepX / 2; x < pW + stepX * 0.35; x += stepX) {
                out.push({ cx: x, cy: y });
                // A pathological stamp (one glyph at size 6) would otherwise ask
                // pdf-lib for tens of thousands of draws per page.
                if (out.length >= 400) return out;
            }
        }
        return out;
    }

    /**
     * places(o) → [{x, y, rot}, …]
     *
     *   o.pageW / o.pageH   page box, PDF points
     *   o.stampW / o.stampH the unrotated stamp, same units
     *   o.position          'top-left' … 'bottom-right' | 'free'
     *   o.freeX / o.freeY   centre as a 0…1 fraction of the page, y UP,
     *                       used when position === 'free' (what dragging sets)
     *   o.rotation          degrees, counter-clockwise
     *   o.tile              repeat across the page
     *   o.tileDensity       columns when tiling
     *   o.margin            keep-out from the page edge for grid positions
     */
    function places(o) {
        o = o || {};
        var pW = o.pageW || 595, pH = o.pageH || 842;
        var w = Math.max(1, o.stampW || 1), h = Math.max(1, o.stampH || 1);
        var deg = Number(o.rotation) || 0;
        var b = bbox(w, h, deg);
        var margin = o.margin == null ? 36 : o.margin;

        var centres;
        if (o.tile) {
            centres = tileCentres(pW, pH, b.w, b.h, o.tileDensity);
        } else if (o.position === 'free') {
            var fx = o.freeX == null ? 0.5 : Number(o.freeX);
            var fy = o.freeY == null ? 0.5 : Number(o.freeY);
            centres = [clampCentre(fx * pW, fy * pH, pW, pH, b.w, b.h)];
        } else {
            centres = [gridCentre(o.position, pW, pH, b.w, b.h, margin)];
        }

        var out = [];
        for (var i = 0; i < centres.length; i++) {
            out.push(anchor(centres[i].cx, centres[i].cy, w, h, deg));
        }
        return out;
    }

    root.WmLayout = {
        places: places,
        bbox: bbox,
        anchor: anchor,
        _gridCentre: gridCentre,
        _tileCentres: tileCentres,
    };
})(typeof self !== 'undefined' ? self : this);
